/* Verono — verono-motion.js (V4 luxury motion layer, 5–6 Sep 2026)
   Companion to verono-motion.css. Loaded by layout/theme.liquid only while
   Theme settings -> Verono motion is on. Five small jobs, all progressive
   enhancement — if this file never runs, every button, photo and bar still
   works and nothing is hidden:
     1. arm the product-gallery crossfade only after the FIRST photo switch,
        so the initial (LCP) image is never faded in on load;
     2. schedule the repeating gold shine (touch screens by default, desktop
        when "idle" is on) as one-shot passes on buttons that are actually in
        view, a few passes each time a button enters view, nothing while the
        tab is hidden — so the page is idle between passes;
     3. replay the shine on tap when the merchant chose "On tap";
     4. pulse the Add-to-cart button the moment it is submitted (the store uses
        the cart PAGE, so Dawn redirects before it would publish 'cart-update';
        drawer/notification carts also get the confirmation pulse);
     5. pop the header cart count when it changes.
   No scroll listeners, no rAF loops, no layout reads in loops. Reduced-motion
   users get none of it (the CSS strips the visuals independently). */
(function () {
  'use strict';
  if (window.__veronoMotion) return;
  window.__veronoMotion = true;

  var doc = document;
  var body = doc.body;
  if (!body) return;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;
  var hasMO = 'MutationObserver' in window;
  var hasIO = 'IntersectionObserver' in window;
  /* Data Saver: no unprompted passes (same gate as kinetic-motion.js); user-initiated effects stay */
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var cls = function (c) { return body.classList.contains(c); };

  /* gold commerce buttons that carry the shine band */
  var SHINE = '.product-form__submit, .sticky-atc .button, .cart__checkout-button';

  /* remove + reflow + add restarts a CSS animation on the same element; one
     timer per element and class, so a re-entry restarts the window instead of
     letting the earlier timer cut the new run short */
  function once(el, name, ms) {
    var t = el.__kvOnce || (el.__kvOnce = {});
    if (t[name]) clearTimeout(t[name]);
    el.classList.remove(name);
    void el.offsetWidth;
    el.classList.add(name);
    t[name] = setTimeout(function () { t[name] = null; el.classList.remove(name); }, ms);
  }
  function token(name, fallback) {
    var v = parseFloat(getComputedStyle(doc.documentElement).getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }
  function shineDur() { return token('--kv-shine-dur', 900); }
  function usable(el) {
    return el.isConnected && !el.disabled && el.getAttribute('aria-disabled') !== 'true' && !el.classList.contains('loading');
  }

  /* 1. gallery crossfade gate */
  function armGalleries(scope) {
    if (!hasMO || !cls('kv-pdp-media')) return;
    var root = scope && scope.querySelectorAll ? scope : doc;
    var galleries = root.querySelectorAll('media-gallery:not([data-kv-armed])');
    Array.prototype.forEach.call(galleries, function (g) {
      g.setAttribute('data-kv-armed', '1');
      var items = g.querySelectorAll('.product__media-item');
      if (!items.length) return;
      var mo = new MutationObserver(function () {
        g.classList.add('kv-gallery-live');
        mo.disconnect();
      });
      Array.prototype.forEach.call(items, function (it) {
        mo.observe(it, { attributes: true, attributeFilter: ['class'] });
      });
    });
  }

  /* 2. scheduled shine passes */
  var periodic = cls('kv-shine') && hasIO && !saveData && (
    (cls('kv-shine-m-periodic') && window.matchMedia && window.matchMedia('(hover: none)').matches) ||
    cls('kv-shine-idle')
  );
  var shineIO = null;
  function stopPass(el) { if (el.__kvT) { clearTimeout(el.__kvT); el.__kvT = null; } }
  function schedulePass(el, delay) {
    stopPass(el);
    el.__kvT = setTimeout(function () {
      el.__kvT = null;
      if (doc.hidden || !el.__kvSeen || !el.isConnected) return;
      var period = token('--kv-shine-period', 6) * 1000;
      var passes = Math.max(1, Math.round(token('--kv-shine-passes', 4)));
      if (!usable(el) || el.classList.contains('kv-added')) { schedulePass(el, period); return; }
      once(el, 'kv-shine-once', shineDur() + 120);
      el.__kvN = (el.__kvN || 0) + 1;
      if (el.__kvN < passes) schedulePass(el, period);
    }, delay);
  }
  function armShine(scope) {
    if (!periodic) return;
    if (!shineIO) {
      shineIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var el = e.target;
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            el.__kvSeen = true; el.__kvN = 0;
            schedulePass(el, 2400); /* first pass once the page has settled */
          } else {
            el.__kvSeen = false;
            stopPass(el);
          }
        });
      }, { threshold: [0, 0.5] });
      doc.addEventListener('visibilitychange', function () {
        Array.prototype.forEach.call(doc.querySelectorAll(SHINE), function (el) {
          if (!el.__kvIO) return;
          if (doc.hidden) stopPass(el);
          else if (el.__kvSeen) { el.__kvN = 0; schedulePass(el, 2400); }
        });
      });
    }
    var root = scope && scope.querySelectorAll ? scope : doc;
    Array.prototype.forEach.call(root.querySelectorAll(SHINE), function (el) {
      if (el.__kvIO) return;
      el.__kvIO = true;
      shineIO.observe(el);
    });
  }

  /* 3. shine on tap (touch / pen only — mouse users get the hover sweep) */
  if (cls('kv-shine') && cls('kv-shine-m-tap')) {
    doc.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      var t = e.target && e.target.closest ? e.target.closest(SHINE) : null;
      if (!t || !usable(t)) return;
      once(t, 'kv-shine-once', shineDur() + 120);
    }, { passive: true });
  }

  /* 4. added-to-cart pulse: on submit (always visible, even when the page
        redirects to the cart) and again on Dawn's confirmation when a drawer /
        notification cart is in use. The pulse skips buttons the shine is
        mid-pass on only by restarting the class, never by hiding anything. */
  if (cls('kv-pdp-added')) {
    var lastForm = null;
    /* only the buttons that own the submitted form: the in-flow button (ancestor
       form) and the sticky-bar button (form="..." attribute) both resolve via .form */
    function pulseForm(form) {
      if (!form) return;
      var buttons = doc.querySelectorAll('.product-form__submit, .sticky-atc .button');
      Array.prototype.forEach.call(buttons, function (b) { if (b.form === form) once(b, 'kv-added', 1600); });
    }
    doc.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.closest || !form.closest('product-form')) return;
      if (!form.querySelector('[name="add"], .product-form__submit')) return;
      lastForm = form;
      pulseForm(form);
    }, true);
    if (typeof window.subscribe === 'function') {
      try {
        window.subscribe('cart-update', function (ev) {
          if (!ev || ev.source !== 'product-form') return;
          pulseForm(lastForm);
        });
      } catch (err) { /* pubsub unavailable: the submit pulse still runs */ }
    }
  }

  /* 5. cart count pop when the number changes (survives the page-cart redirect) */
  if (cls('kv-cart-pop')) {
    var KEY = 'verono:cart-count:v1';
    function bubble() { return doc.querySelector('#cart-icon-bubble .cart-count-bubble'); }
    function count() {
      var b = bubble();
      if (!b) return 0;
      var n = b.querySelector('span[aria-hidden]');
      var v = parseInt(n ? n.textContent : '0', 10);
      return isNaN(v) ? 0 : v;
    }
    function check() {
      var n = count(), prev = null;
      try { prev = sessionStorage.getItem(KEY); } catch (err) { prev = null; }
      if (prev !== null && String(n) !== prev && n > 0) {
        var b = bubble();
        if (b) once(b, 'kv-pop', 700);
      }
      try { sessionStorage.setItem(KEY, String(n)); } catch (err) { /* storage unavailable */ }
    }
    check();
    var host = doc.getElementById('cart-icon-bubble');
    if (host && hasMO) new MutationObserver(function () { check(); }).observe(host, { childList: true, subtree: true });
  }

  function init(scope) { armGalleries(scope); armShine(scope); }
  init(doc);
  doc.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
